import { useRef, useState, type DragEvent } from 'react';
import Button from '@/components/base/Button';
import { DownloadTemplatePanel } from './BulkSidePanels';

interface FileUploadPanelProps {
  channel: 'wallet' | 'mobile';
  onParse: (fileName: string) => void;
  onDownloadTemplate: (format: 'excel' | 'csv') => void;
}

const acceptedExtensions = ['.xlsx', '.xls', '.csv'];
const maxSizeBytes = 10 * 1024 * 1024;

function formatFileSize(bytes: number): string {
  if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  if (bytes >= 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${bytes} B`;
}

function validateFile(file: File): string | null {
  const lower = file.name.toLowerCase();
  if (!acceptedExtensions.some((ext) => lower.endsWith(ext))) {
    return 'Unsupported file type. Upload an Excel (.xlsx, .xls) or CSV (.csv) file.';
  }
  if (file.size > maxSizeBytes) {
    return `File is too large (${formatFileSize(file.size)}). Maximum size is 10 MB.`;
  }
  return null;
}

export default function FileUploadPanel({ channel, onParse, onDownloadTemplate }: FileUploadPanelProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [fileSize, setFileSize] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);

  const acceptFile = (file: File) => {
    const message = validateFile(file);
    if (message) {
      setError(message);
      setFileName(null);
      setFileSize(0);
      return;
    }
    setError(null);
    setFileName(file.name);
    setFileSize(file.size);
  };

  const clearFile = () => {
    setFileName(null);
    setFileSize(0);
    setError(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleDrop = (event: DragEvent<HTMLDivElement>) => {
    event.preventDefault();
    setDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) acceptFile(file);
  };

  const zoneState = dragging
    ? 'border-primary-400 bg-primary-50'
    : error
      ? 'border-accent-400 bg-accent-50'
      : 'border-background-300 bg-background-100';

  return (
    <div className="space-y-4">
      <DownloadTemplatePanel channel={channel} onDownload={onDownloadTemplate} />

      <div>
        <span className="text-xs font-label font-medium text-foreground-600">
          Transaction File <span className="text-primary-600">*</span>
        </span>

        <input
          ref={inputRef}
          id="bulk-file"
          type="file"
          accept=".xlsx,.xls,.csv"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) acceptFile(file);
          }}
          className="sr-only"
        />

        <div
          role="button"
          tabIndex={0}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              inputRef.current?.click();
            }
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setDragging(true);
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            setDragging(false);
          }}
          onDrop={handleDrop}
          className={`mt-1.5 flex cursor-pointer flex-col items-center justify-center rounded-lg border-2 border-dashed px-5 py-7 text-center transition-colors ${zoneState}`}
        >
          <span
            className={`flex h-11 w-11 items-center justify-center rounded-full text-lg ${
              dragging ? 'bg-primary-100 text-primary-700' : 'bg-background-200 text-foreground-500'
            }`}
          >
            <i className="ri-upload-cloud-2-line" />
          </span>
          <p className="mt-3 text-sm font-medium text-foreground-800">
            {dragging ? 'Drop your file to upload' : 'Drag & drop your file here'}
          </p>
          <p className="mt-1 text-xs text-foreground-500">
            or <span className="font-medium text-primary-700">browse</span> from your device
          </p>
        </div>

        {fileName ? (
          <div className="mt-2 flex items-center justify-between gap-3 rounded-md border border-background-200 bg-background-50 px-3 py-2">
            <span className="inline-flex min-w-0 items-center gap-2 text-sm text-foreground-800">
              <i className="ri-file-excel-2-line text-base text-primary-600" />
              <span className="truncate">{fileName}</span>
              <span className="shrink-0 text-xs text-foreground-500">{formatFileSize(fileSize)}</span>
            </span>
            <button
              type="button"
              onClick={clearFile}
              aria-label="Remove file"
              className="shrink-0 cursor-pointer text-foreground-500 transition-colors hover:text-foreground-900"
            >
              <i className="ri-close-line" />
            </button>
          </div>
        ) : null}

        {error ? (
          <p className="mt-2 inline-flex items-center gap-1.5 text-xs text-accent-800">
            <i className="ri-error-warning-line" />
            {error}
          </p>
        ) : (
          <p className="mt-1.5 text-xs text-foreground-500">
            Excel (.xlsx, .xls) or CSV — max 10 MB. The template columns map to the{' '}
            {channel === 'wallet' ? 'Twiva wallet ID' : 'mobile number'} field, and an optional{' '}
            <span className="font-label text-foreground-700">payment_date</span> column schedules a
            row for a specific day and time.
          </p>
        )}
      </div>

      <Button
        icon="ri-upload-cloud-2-line"
        onClick={() => onParse(fileName ?? 'bulk-recipients.xlsx')}
        disabled={!fileName}
      >
        Upload &amp; Preview
      </Button>
    </div>
  );
}